import queryString from 'query-string';
import { getEnvConfig } from 'src/helpers/config';
import type {
  CreateAliasesBody,
  CreateAliasesResponse,
  DeleteAliasParams,
  GetAliasesResponse,
  GetAliasesSuggestionsBody,
  GetAliasesSuggestionsResponse,
  RejectAliasSuggestionBody,
} from 'src/types/aliases';
import type {
  GetCatalogGameCreationResultResponse,
  GetCatalogGameCreationResultsResponse,
  GetCatalogGameMappingsResponse,
  GetCatalogGamesBody,
  GetCatalogGamesFiltersResponse,
  GetCatalogGamesResponse,
  GetCatalogGamesSegmentsBody,
  GetCatalogGamesSegmentsResponse,
  TriggerCatalogGamesCreationResultBody,
  TriggerCatalogGamesCreationResultResponse,
} from 'src/types/catalog-games';
import type {
  GameMatchResult,
  GameMatchStatusResponse,
  GetGameMatchResultsResponse,
  TriggerGameMatchBody,
  UpdateGameMatchResultBody,
} from 'src/types/game-match';
import type {
  GetPartnerGamesMappingResultsResponse,
  GetPartnerGamesMappingStatsResponse,
  GetPromotedPartnerGamesBody,
  GetPromotedPartnerGamesResponse,
  GetUnmappedPartnerGamesQuery,
  GetUnmappedPartnerGamesResponse,
  PromotedPartnerGamesTransformed,
  TriggerPartnerGamesMappingResultBody,
  TriggerPartnerGamesMappingResultResponse,
  UpdatePartnerGamesBody,
  UpdatePartnerGamesResponse,
} from 'src/types/partner-games';
import type { GetPlatformsResponse, UpdateBrandsPayload, UpdateBrandsResponse } from 'src/types/platforms';
import type { GetUserResponse, LoginBody } from 'src/types/users';
import { getFetchMethod, HttpMethod } from './common';
import { transformPromotedPartnerGamesResponse } from './transformers/partner-games';

const getApiUrl = () => getEnvConfig().apiUrl;

export const signIn = (data: LoginBody) =>
  getFetchMethod({
    url: `${getApiUrl()}/users/login`,
    method: HttpMethod.POST,
    data,
  });

export const signOut = () =>
  getFetchMethod({
    url: `${getApiUrl()}/users/logout`,
    method: HttpMethod.POST,
    data: {},
  });

export const getUser = () =>
  getFetchMethod<GetUserResponse>({
    url: `${getApiUrl()}/users/me`,
  });

export const getPlatforms = () =>
  getFetchMethod<GetPlatformsResponse>({
    url: `${getApiUrl()}/platforms`,
  });

export const updateBrands = (data: UpdateBrandsPayload) =>
  getFetchMethod<UpdateBrandsResponse>({
    url: `${getApiUrl()}/brands`,
    method: HttpMethod.PATCH,
    data,
  });

export const getAliases = () =>
  getFetchMethod<GetAliasesResponse>({
    url: `${getApiUrl()}/aliases`,
  });

export const createAliases = (data: CreateAliasesBody) =>
  getFetchMethod<CreateAliasesResponse>({
    url: `${getApiUrl()}/aliases`,
    method: HttpMethod.POST,
    data,
  });

export const deleteAlias = (data: DeleteAliasParams) =>
  getFetchMethod({
    url: `${getApiUrl()}/aliases/${encodeURIComponent(data.gameProviderName)}/${encodeURIComponent(data.alias)}`,
    method: HttpMethod.DELETE,
  });

export const getAliasesSuggestions = (data: GetAliasesSuggestionsBody) =>
  getFetchMethod<GetAliasesSuggestionsResponse>({
    url: `${getApiUrl()}/aliases/suggestions`,
    method: HttpMethod.POST,
    data,
  });

export const rejectAliasSuggestion = (data: RejectAliasSuggestionBody) =>
  getFetchMethod({
    url: `${getApiUrl()}/aliases/suggestions/rejects`,
    method: HttpMethod.POST,
    data,
  });

export const getCatalogGamesFilters = () =>
  getFetchMethod<GetCatalogGamesFiltersResponse>({
    url: `${getApiUrl()}/catalog-games/filters`,
  });

export const getCatalogGames = (data: GetCatalogGamesBody) =>
  getFetchMethod<GetCatalogGamesResponse>({
    url: `${getApiUrl()}/catalog-games/search`,
    method: HttpMethod.POST,
    data,
  });

export const triggerCreationResults = (data: TriggerCatalogGamesCreationResultBody) =>
  getFetchMethod<TriggerCatalogGamesCreationResultResponse>({
    url: `${getApiUrl()}/catalog-games/creation-results/trigger`,
    method: HttpMethod.POST,
    data,
  });

export const deleteCatalogGame = (id: number) =>
  getFetchMethod({
    url: `${getApiUrl()}/catalog-games/${id}`,
    method: HttpMethod.DELETE,
  });

export const getCatalogGameMappings = (id: number) =>
  getFetchMethod<GetCatalogGameMappingsResponse>({
    url: `${getApiUrl()}/catalog-games/${id}/mappings`,
  });

export const getCatalogGamesSegments = (data: GetCatalogGamesSegmentsBody) =>
  getFetchMethod<GetCatalogGamesSegmentsResponse>({
    url: `${getApiUrl()}/catalog-games/segments/search`,
    method: HttpMethod.POST,
    data,
  });

export const getCatalogGamesCreationResults = () =>
  getFetchMethod<GetCatalogGameCreationResultsResponse>({
    url: `${getApiUrl()}/catalog-games/creation-results`,
  });

export const getCatalogGamesCreationResult = (id: string) =>
  getFetchMethod<GetCatalogGameCreationResultResponse>({
    url: `${getApiUrl()}/catalog-games/creation-results/${id}`,
  });

export const getPartnerGamesMappingStats = () =>
  getFetchMethod<GetPartnerGamesMappingStatsResponse>({
    url: `${getApiUrl()}/partner-games/mapping-stats`,
  });

export const getUnmappedPartnerGames = (data: GetUnmappedPartnerGamesQuery) =>
  getFetchMethod<GetUnmappedPartnerGamesResponse>({
    url: `${getApiUrl()}/partner-games/unmapped?${queryString.stringify(data)}`,
  });

export const updatePartnerGames = (data: UpdatePartnerGamesBody) =>
  getFetchMethod<UpdatePartnerGamesResponse>({
    url: `${getApiUrl()}/partner-games`,
    method: HttpMethod.PATCH,
    data,
  });

export const getMappingResults = () =>
  getFetchMethod<GetPartnerGamesMappingResultsResponse>({
    url: `${getApiUrl()}/partner-games/mapping-results`,
  });

export const triggerMappingResults = (data: TriggerPartnerGamesMappingResultBody) =>
  getFetchMethod<TriggerPartnerGamesMappingResultResponse>({
    url: `${getApiUrl()}/partner-games/mapping-results/trigger`,
    method: HttpMethod.POST,
    data,
  });

export const getPromotedPartnerGames = (data: GetPromotedPartnerGamesBody) =>
  getFetchMethod<GetPromotedPartnerGamesResponse, PromotedPartnerGamesTransformed>(
    {
      url: `${getApiUrl()}/partner-games/promoted/search`,
      method: HttpMethod.POST,
      data,
    },
    transformPromotedPartnerGamesResponse,
  );

export const triggerGameMatch = (data: TriggerGameMatchBody) =>
  getFetchMethod<GameMatchStatusResponse>({
    url: `${getApiUrl()}/game-match/trigger`,
    method: HttpMethod.POST,
    data,
  });

export const getGameMatchStatus = () =>
  getFetchMethod<GameMatchStatusResponse>({
    url: `${getApiUrl()}/game-match/status`,
  });

export const getGameMatchResults = () =>
  getFetchMethod<GetGameMatchResultsResponse>({
    url: `${getApiUrl()}/game-match/results`,
  });

export const updateGameMatchResult = (data: UpdateGameMatchResultBody) =>
  getFetchMethod<GameMatchResult>({
    url: `${getApiUrl()}/game-match/results`,
    method: HttpMethod.PATCH,
    data,
  });

export const applyGameMatch = () =>
  getFetchMethod<unknown>({
    url: `${getApiUrl()}/game-match/apply`,
    method: HttpMethod.POST,
    data: {},
  });
